import React, { useEffect, useState } from 'react'
import {
  Alert,
  BodyText,
  Button,
  Col,
  Container,
  Dialog,
  HeadingText,
  IconButton,
  Row,
  Sheet,
  ThemeProvider,
  V2ArrowLeftIcon,
} from '@paypalcorp/pp-react'
import PrivacySpinner from '../../components/spinner'
import { fptiCustomSetup, fptiCustomClick, serverData } from '../../utils'
import { post } from '../../utils/ajax'
import { faceScanConfig } from '../../config/tether-id'
import { Message } from '@paypalcorp/worldready-react'
import DATAPRIVACY_FPTI from '../../config/data-privacy-fpti'
import { SESSION_TIMEOUT } from '../../constants/constants'

const PRIVACY_URL = '/myaccount/privacy'
const DELETE_VERIFICATION_API = '/myaccount/privacy/data/verify/api/facescan'

const FaceScan = () => {
  const { faceScanStatus, isNative, _csrf } = serverData
  const [config, setConfig] = useState(faceScanConfig.inactive)
  const [modal, setModal] = useState({ content: {}, onClick: null })
  const [isError, setIsError] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    fptiCustomSetup(DATAPRIVACY_FPTI.TETHER_ID.FACESCAN.IMPRESSION)
    if (faceScanStatus === null || faceScanStatus === undefined)
      setIsError(true)
    else setConfig(faceScanConfig[faceScanStatus])
  }, [])

  const navigateTo = (url) => (window.location.href = url)
  const scrollToTop = () => window.scrollTo({ top: 0 })

  const onDeletionSuccess = () => {
    setIsLoading(true)
    setConfig(faceScanConfig.inactive)
    setModal({ content: {}, onClick: null })
    setIsLoading(false)
    scrollToTop()
  }

  const onClose = () => {
    setIsOpen(false)
    if (success) onDeletionSuccess()
  }

  const deleteVerification = async () => {
    try {
      const response = await post(DELETE_VERIFICATION_API, { _csrf })
      if (response.success) {
        if (response.data.requestUrl) {
          setIsLoading(false)
          setIsError(true)
          scrollToTop()
          setTimeout(function () {
            navigateTo(response.data.requestUrl)
          }, SESSION_TIMEOUT)
          return
        }
        setSuccess(true)
        setIsError(false)
        setModal({
          content: config.modal.confirm,
          onClick: () => onDeletionSuccess(),
        })
        setIsOpen(true)
      } else {
        setIsError(true)
        scrollToTop()
      }
    } catch (error) {
      setIsError(true)
      scrollToTop()
    }
    setIsLoading(false)
  }

  const Modal = ({ children }) => {
    if (isNative)
      return (
        <Sheet id="faceScanModal" isOpen={isOpen} onClose={onClose}>
          {children}
        </Sheet>
      )
    return (
      <Dialog
        id="faceScanModal"
        isOpen={isOpen}
        onClose={onClose}
        closeOnBackgroundClick={true}
      >
        {children}
      </Dialog>
    )
  }

  const modalContent = ({ content, onClick }) => (
    <Container fluid id="tid-modal" className="tid-modal-content">
      {content.icon && (
        <Row>
          <Col className="tid-modal-content__icon">{content.icon}</Col>
        </Row>
      )}
      {content.title && (
        <Row>
          <Col className="tid-modal-content__title">
            <HeadingText size="sm" id="faceScanModalTitle">
              <Message id={content.title} />
            </HeadingText>
          </Col>
        </Row>
      )}
      {content.desc && (
        <Row>
          <Col className="tid-modal-content__desc">
            <BodyText id="faceScanModalDesc">
              <Message id={content.desc} />
            </BodyText>
          </Col>
        </Row>
      )}
      {content.button && (
        <Row>
          <Col className="tid-modal-content__button">
            <Button
              id={content.button.id}
              name={content.button.name}
              data-testid={content.button.id}
              onClick={onClick}
            >
              <Message id={content.button.text} />
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  )

  const tidNav = () => (
    <Row align="center">
      <Col xs={12} sm={10} md={12}>
        <IconButton
          id="faceScanMoveBackBtn"
          data-testid="faceScanMoveBackBtn"
          buttonType="tertiary"
          svgIconComponent={V2ArrowLeftIcon}
          onClick={() => {
            fptiCustomClick(
              DATAPRIVACY_FPTI.TETHER_ID.FACESCAN.REDIRECT_PRIVACY_LANDING_PAGE,
            )
            navigateTo(PRIVACY_URL)
          }}
        >
          <Message id="tetherId.facescan.nav.move.back" />
        </IconButton>
      </Col>
    </Row>
  )

  const tidError = () => (
    <Container fluid className="tid-error">
      <Row>
        <Col className="tid-error__message">
          <Alert type="error" theme="v1">
            <Message id={config.error.message} />
          </Alert>
        </Col>
      </Row>
    </Container>
  )

  const tidHeader = () => (
    <Container fluid className="tid-header">
      <Row>
        <Col className="tid-header__image">{config.header.image}</Col>
      </Row>
      <Row>
        <Col className="tid-header__title">
          <HeadingText size="sm" id="faceScanHeaderTitle">
            <Message id={config.header.title} />
          </HeadingText>
        </Col>
      </Row>
      <Row>
        <Col className="tid-header__status">
          <BodyText id="faceScanHeaderStatus">
            <Message id={config.header.status} />
          </BodyText>
        </Col>
      </Row>
    </Container>
  )

  const tidLine = () => (
    <Container fluid className="tid-divider">
      <Row>
        <Col>
          <div className="tid-divider__line" />
        </Col>
      </Row>
    </Container>
  )

  const tidContent = () => (
    <Container fluid className="tid-content">
      <Row>
        <Col>
          {config.contents.map((content, index) => (
            <Container
              key={content.title || content.desc}
              fluid
              className="tid-content__container"
            >
              {content.title && (
                <Row>
                  <Col className="tid-content__title">
                    <BodyText strong id={`faceScanContentTitle${index}`}>
                      <Message id={content.title} />
                    </BodyText>
                  </Col>
                </Row>
              )}
              <Row>
                <Col className="tid-content__desc">
                  {content.isList ? (
                    <ul className="tid-content__desc-unordered-list">
                      {content.desc.map((text, descIndex) => (
                        <li key={text} className="tid-content__desc-list">
                          <BodyText
                            className="tid-content__desc-text"
                            id={`faceScanContentDesc${descIndex}`}
                          >
                            <Message id={text} />
                          </BodyText>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <BodyText
                      className="tid-content__desc-text"
                      id="faceScanContentDesc"
                    >
                      <Message id={content.desc} />
                    </BodyText>
                  )}
                </Col>
              </Row>
            </Container>
          ))}
        </Col>
      </Row>
    </Container>
  )

  const tidButton = () => (
    <Container fluid className="tid-button">
      <Row align="center">
        <Col colSize="auto" className="tid-button__container">
          <Button
            id={config.button.id}
            name={config.button.name}
            data-testid={config.button.id}
            secondary
            onClick={() => {
              setIsOpen(true)
              // For second modal
              setModal({
                content: config.modal.req,
                onClick: () => {
                  setIsOpen(false)
                  setIsLoading(true)
                  deleteVerification()
                },
              })
            }}
          >
            <Message id={config.button.text} />
          </Button>
        </Col>
      </Row>
    </Container>
  )

  return (
    <ThemeProvider theme="v2">
      <Container fluid id="tether-id">
        {tidNav()}
        <Row align="center">
          <Col xs={12} sm={10} md={8}>
            {isError && tidError()}
            {tidHeader()}
            {tidLine()}
            {tidContent()}
            {config.button && tidButton()}
          </Col>
        </Row>
        {config.modal && <Modal>{modalContent(modal)}</Modal>}
        {isLoading && <PrivacySpinner />}
      </Container>
    </ThemeProvider>
  )
}

export default FaceScan
